import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { generateCode128Svg } from '@shared/utils/code128.util';

import { LotBarcodeLabelControllerService } from '@app/api/stock-service/api/lotBarcodeLabelController.service';
import { LotBarcodeLabelVO } from '@app/api/stock-service/model/lotBarcodeLabelVO';
import { ThemeService } from '@app/core/core';
import { PermissionService } from '@app/core/services/permission.service';
import {
  LOT_BARCODE_COLUMNS,
  LOT_BARCODE_DEFAULT_COLUMNS_ORDER,
  LOT_BARCODE_FLEXIBLE_COLUMNS_ORDER,
} from './lot-barcode.config';
import { LotBarcodeCreateDialogComponent } from './components/lot-barcode-create-dialog/lot-barcode-create-dialog.component';

const LAZY_BATCH_SIZE = 20;

@Component({
  selector: 'app-lot-barcode',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    AppButtonComponent,
    AdvancedDataTableComponent,
    SearchFormComponent,
    ToastModule,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './lot-barcode.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotBarcodeComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly lotBarcodeApi = inject(LotBarcodeLabelControllerService);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly themeService = inject(ThemeService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  private dialogRef: DynamicDialogRef | null = null;

  rows = signal<LotBarcodeLabelVO[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  pageSize = 20;
  page = signal(0);

  private renderedBarcodeCount = signal(LAZY_BATCH_SIZE);
  private barcodeCache = new Map<string, SafeHtml>();

  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.product.lot_barcode.view');
  });

  canPrint = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.product.lot_barcode.print');
  });

  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.product.lot_barcode.create');
  });

  searchFormConfig: FormFieldConfig[] = [
    {
      key: 'skuCode',
      label: 'lot_barcode.search.sku_code',
      type: 'text',
      placeholder: 'lot_barcode.search.enter_sku_code',
    },
    {
      key: 'skuName',
      label: 'lot_barcode.search.sku_name',
      type: 'text',
      placeholder: 'lot_barcode.search.enter_sku_name',
    },
    {
      key: 'lotValue',
      label: 'lot_barcode.search.lot_value',
      type: 'text',
      placeholder: 'lot_barcode.search.enter_lot_value',
    },
    {
      key: 'expiry',
      label: 'lot_barcode.search.expiry_range',
      type: 'dateRange',
      range: {
        startKey: 'expiryFrom',
        endKey: 'expiryTo',
        startLabel: 'lot_barcode.search.expiry_from',
        endLabel: 'lot_barcode.search.expiry_to',
      },
    },
  ];

  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      LOT_BARCODE_COLUMNS.find((c) => c.prop === prop);
    const cols: TableColumn[] = [];
    for (const key of LOT_BARCODE_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }
    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      LOT_BARCODE_COLUMNS.find((c) => c.prop === prop);
    const cols: TableColumn[] = [];
    for (const prop of LOT_BARCODE_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) cols.push(col);
    }
    return cols;
  });

  private searchParams: Record<string, any> = {};

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.loading.set(true);
    this.barcodeCache.clear();
    this.renderedBarcodeCount.set(LAZY_BATCH_SIZE);

    const params: Record<string, any> = {
      page: this.page(),
      size: this.pageSize,
    };

    if (this.searchParams['skuCode']) {
      params['skuCode'] = this.searchParams['skuCode'];
    }
    if (this.searchParams['skuName']) {
      params['skuName'] = this.searchParams['skuName'];
    }
    if (this.searchParams['lotValue']) {
      params['lotValue'] = this.searchParams['lotValue'];
    }
    if (this.searchParams['expiryFrom']) {
      params['expiryFrom'] = this.formatDateParam(this.searchParams['expiryFrom']);
    }
    if (this.searchParams['expiryTo']) {
      params['expiryTo'] = this.formatDateParam(this.searchParams['expiryTo']);
    }

    this.lotBarcodeApi
      .page(params as any)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.rows.set(res.pageContent ?? []);
          this.totalRecords.set(res.totalElements ?? 0);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('lot_barcode.messages.load_error'),
          });
        },
      });
  }

  private formatDateParam(value: any): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  onSearch(searchData: any): void {
    this.searchParams = { ...searchData };
    this.page.set(0);
    this.loadData();
  }

  onReset(): void {
    this.searchParams = {};
    this.page.set(0);
    this.loadData();
  }

  onPageChange(event: any): void {
    this.page.set(event.page);
    this.pageSize = event.rows;
    this.loadData();
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;
    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  getBarcodeHtml(row: LotBarcodeLabelVO, index: number): SafeHtml | null {
    const payload = row.barcodePayload;
    if (!payload || index >= this.renderedBarcodeCount()) {
      return null;
    }
    let cached = this.barcodeCache.get(payload);
    if (!cached) {
      const svg = generateCode128Svg(payload, 40);
      cached = this.sanitizer.bypassSecurityTrustHtml(svg);
      this.barcodeCache.set(payload, cached);
    }
    return cached;
  }

  onTableScroll(): void {
    const current = this.renderedBarcodeCount();
    if (current < this.rows().length) {
      this.renderedBarcodeCount.set(
        Math.min(current + LAZY_BATCH_SIZE, this.rows().length),
      );
    }
  }

  formatExpiryDate(dateStr: string | undefined): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  onCreate(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.dialogRef = this.dialogService.open(LotBarcodeCreateDialogComponent, {
      header: this.translateService.instant('lot_barcode.dialog.create_title'),
      width: '500px',
      modal: true,
      closable: true,
    });

    this.dialogRef.onClose.subscribe((result: any) => {
      if (result) {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant('lot_barcode.messages.create_success'),
        });
        this.loadData();
      }
    });
  }

  onPrint(): void {
    const currentRows = this.rows();
    if (!currentRows.length) {
      return;
    }

    const cards = currentRows
      .map((row) => {
        const payload = row.barcodePayload ?? '';
        const svg = payload ? generateCode128Svg(payload, 50) : '';
        return `
          <div class="barcode-card">
            <div class="info"><strong>${row.skuCode ?? ''}</strong> — ${row.skuName ?? ''}</div>
            ${row.lotValue ? `<div class="lot">Lot: ${row.lotValue}</div>` : ''}
            ${row.expiryDate ? `<div class="expiry">Exp: ${this.formatExpiryDate(row.expiryDate)}</div>` : ''}
            <div class="barcode">${svg}</div>
            <div class="value">${payload}</div>
          </div>`;
      })
      .join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Lot Barcodes</title>
      <style>
        body{font-family:Arial,sans-serif;margin:20px}
        .barcode-card{border:1px solid #ccc;padding:12px;margin:8px 0;page-break-inside:avoid;text-align:center}
        .info{margin-bottom:4px;font-size:13px}
        .lot,.expiry{font-size:11px;color:#555}
        .barcode svg{max-width:300px;height:50px}
        .value{font-size:11px;margin-top:4px;letter-spacing:1px;word-break:break-all}
        @media print{body{margin:0}.barcode-card{border:none;border-bottom:1px solid #eee}}
      </style></head><body>${cards}</body></html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    }
  }
}
